import React, { useState, useEffect, FC, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DatePicker from '../../components/ui/DatePicker';
import ProgressBar from '../../components/ui/ProgressBar';
import Badge from '../../components/ui/Badge';
import Stepper from '../../components/ui/Stepper';
import Loader from '../../components/ui/Loader';
import { UserDashboardFormData, UserDashboardFormErrors, StepDefinition } from '../../types/components';
import { LocalStorageUser } from '../../types/localStorage';
import Toast from '../../components/ui/Toast';

const DashboardForm: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [formData, setFormData] = useState<UserDashboardFormData>({
    // Personal Information
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    currentAddress: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    
    // Professional Information
    professionalHeadline: '',
    careerObjective: '',
    keySkills: '',
    experience: '',
    currentCompany: '',
    currentPosition: '',
    expectedSalary: '',
    noticePeriod: '',
    
    // Job Preferences
    preferredJobTypes: [],
    preferredWorkModes: [],
    preferredLocations: '',
    preferredIndustries: [],
    
    // Additional Information
    linkedinUrl: '',
    githubUrl: '',
    portfolioUrl: '',
    resume: null,
    coverLetter: '',
    
    // Notification Preferences
    emailNotifications: true,
    smsNotifications: false,
    applicationUpdates: true,
    jobRecommendations: true,
  });

  const steps: StepDefinition[] = [
    { title: 'Personal Info', description: 'Basics & contact', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { title: 'Professional', description: 'Summary & skills', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6' },
    { title: 'Education', description: 'Education & docs', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { title: 'Preferences', description: 'Jobs & locations', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94.826-3.31-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
    { title: 'Complete', description: 'Review & finish', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' }
  ];

  const [errors, setErrors] = useState<UserDashboardFormErrors>({});

  const updateFormData = (newData: Partial<UserDashboardFormData>) => {
    setFormData(prev => ({ ...prev, ...newData }));
    // Clear errors when user starts typing
    const errorKeys = Object.keys(newData);
    setErrors(prev => {
      const newErrors = { ...prev };
      errorKeys.forEach(key => {
        if (newErrors[key as keyof UserDashboardFormErrors]) {
          delete newErrors[key as keyof UserDashboardFormErrors];
        }
      });
      return newErrors;
    });
  };

  const validateStep = (step: number): boolean => {
    const newErrors: UserDashboardFormErrors = {};
    
    switch (step) {
      case 0: // Personal Info
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.phone) newErrors.phone = 'Phone number is required';
        if (!formData.city) newErrors.city = 'City is required';
        break;
      case 1: // Professional
        if (!formData.professionalHeadline) newErrors.professionalHeadline = 'Professional headline is required';
        if (!formData.keySkills) newErrors.keySkills = 'Key skills are required';
        if (!formData.experience) newErrors.experience = 'Experience level is required';
        break;
      case 2: // Preferences
        if (formData.preferredJobTypes.length === 0) newErrors.preferredJobTypes = 'Please select at least one job type';
        if (formData.preferredWorkModes.length === 0) newErrors.preferredWorkModes = 'Please select at least one work mode';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    // e.preventDefault(); // TODO check
    setIsLoading(true);
    try {
      // Validate all required fields
      const requiredFields: { [key: string]: string } = {
        firstName: 'First name is required',
        lastName: 'Last name is required',
        phone: 'Phone number is required',
        city: 'City is required',
        professionalHeadline: 'Professional headline is required',
        keySkills: 'Key skills are required',
        experience: 'Experience level is required'
      };

      const validationErrors: UserDashboardFormErrors = {};
      Object.entries(requiredFields).forEach(([field, message]) => {
        if (!formData[field as keyof UserDashboardFormData]) {
          validationErrors[field as keyof UserDashboardFormErrors] = message;
        }
      });

      if (formData.preferredJobTypes.length === 0) {
        validationErrors.preferredJobTypes = 'Please select at least one job type';
      }

      if (formData.preferredWorkModes.length === 0) {
        validationErrors.preferredWorkModes = 'Please select at least one work mode';
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setCurrentStep(0); // Go back to first step
        return;
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Save form data to localStorage or send to API
      localStorage.setItem('userProfileComplete', 'true');
      localStorage.setItem('userProfileData', JSON.stringify(formData));
      
      // Show success message
      setToast({ message: 'Profile completed successfully!', type: 'success' });
      
      // Redirect to dashboard
      navigate('/user/dashboard');
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setToast({ message: 'Error saving profile. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = (): void => {
    navigate('/user/dashboard');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Personal Information
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-2">Personal Information</h2>
              <p className="text-gray-600">Let's start with your basic information</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="First Name *"
                name="firstName"
                value={formData.firstName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ firstName: e.target.value })}
                error={errors.firstName}
                required
              />
              <Input
                label="Last Name *"
                name="lastName"
                value={formData.lastName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ lastName: e.target.value })}
                error={errors.lastName}
                required
              />
              <Input
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ email: e.target.value })}
                disabled
                className="bg-gray-50"
              />
              <Input
                label="Phone Number *"
                name="phone"
                value={formData.phone}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ phone: e.target.value })}
                error={errors.phone}
                required
              />
              <DatePicker
                label="Date of Birth"
                value={formData.dateOfBirth}
                onChange={(val) => { 
                  if (typeof val == "string") {
                    updateFormData({ dateOfBirth: val });
                  }
                }}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFormData({ gender: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-200"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Input
                    label="Address"
                    name="currentAddress"
                    value={formData.currentAddress}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ currentAddress: e.target.value })}
                    placeholder="Street address, apartment, suite, etc."
                  />
                </div>
                <Input
                  label="City *"
                  name="city"
                  value={formData.city}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ city: e.target.value })}
                  error={errors.city}
                  required
                />
                <Input
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ state: e.target.value })}
                />
                <Input
                  label="PIN Code"
                  name="pincode"
                  value={formData.pincode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ pincode: e.target.value })}
                />
                <Input
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ country: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 1: // Professional Information
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-2">Professional Information</h2>
              <p className="text-gray-600">Tell us about your professional background</p>
            </div>

            <Input
              label="Professional Headline *"
              name="professionalHeadline"
              value={formData.professionalHeadline}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ professionalHeadline: e.target.value })}
              error={errors.professionalHeadline}
              placeholder="e.g. Senior React Developer with 5+ years experience"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Career Objective/Summary</label>
              <textarea
                name="careerObjective"
                rows={4}
                value={formData.careerObjective}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateFormData({ careerObjective: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-200"
                placeholder="Tell us about your career goals and what you bring to the table..."
              />
            </div>

            <Input
              label="Key Skills *"
              name="keySkills"
              value={formData.keySkills}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ keySkills: e.target.value })}
              error={errors.keySkills}
              placeholder="React, Node.js, JavaScript, Python, AWS, Docker"
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level *</label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFormData({ experience: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-200"
                >
                  <option value="">Select Experience Level</option>
                  <option value="fresher">Fresher (0-1 years)</option>
                  <option value="1-2">1-2 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
                {errors.experience && <p className="text-sm text-red-600 mt-1">{errors.experience}</p>}
              </div>
              <Input
                label="Current Company"
                name="currentCompany"
                value={formData.currentCompany}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ currentCompany: e.target.value })}
                placeholder="Your current company name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Current Position"
                name="currentPosition"
                value={formData.currentPosition}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ currentPosition: e.target.value })}
                placeholder="Your current job title"
              />
              <Input
                label="Expected Salary (LPA)"
                name="expectedSalary"
                type="number"
                value={formData.expectedSalary}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ expectedSalary: e.target.value })}
                placeholder="e.g. 8"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notice Period</label>
              <select
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFormData({ noticePeriod: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-200"
              >
                <option value="">Select Notice Period</option>
                <option value="immediate">Immediate</option>
                <option value="15-days">15 days</option>
                <option value="1-month">1 month</option>
                <option value="2-months">2 months</option>
                <option value="3-months">3 months</option>
              </select>
            </div>
          </div>
        );

      case 2: // Job Preferences
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-2">Job Preferences</h2>
              <p className="text-gray-600">Help us find the right opportunities for you</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Preferred Job Types *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'].map((type) => (
                  <label key={type} className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-primary-lightGreen transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.preferredJobTypes.includes(type)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const newTypes = e.target.checked
                          ? [...formData.preferredJobTypes, type]
                          : formData.preferredJobTypes.filter(t => t !== type);
                        updateFormData({ preferredJobTypes: newTypes });
                      }}
                      className="h-4 w-4 text-primary-lightGreen focus:ring-primary-lightGreen border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">{type}</span>
                  </label>
                ))}
              </div>
              {errors.preferredJobTypes && <p className="text-sm text-red-600 mt-1">{errors.preferredJobTypes}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Work Mode Preference *</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['Remote', 'Hybrid', 'On-site'].map((mode) => (
                  <label key={mode} className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-primary-lightGreen transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.preferredWorkModes.includes(mode)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const newModes = e.target.checked
                          ? [...formData.preferredWorkModes, mode]
                          : formData.preferredWorkModes.filter(m => m !== mode);
                        updateFormData({ preferredWorkModes: newModes });
                      }}
                      className="h-4 w-4 text-primary-lightGreen focus:ring-primary-lightGreen border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">{mode}</span>
                  </label>
                ))}
              </div>
              {errors.preferredWorkModes && <p className="text-sm text-red-600 mt-1">{errors.preferredWorkModes}</p>}
            </div>

            <Input
              label="Preferred Locations"
              name="preferredLocations"
              value={formData.preferredLocations}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ preferredLocations: e.target.value })}
              placeholder="Mumbai, Bangalore, Remote"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Industry Preferences</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing', 'Retail', 'Consulting', 'Other'].map((industry) => (
                  <label key={industry} className="flex items-center p-2 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-lightGreen transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.preferredIndustries.includes(industry)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const newIndustries = e.target.checked
                          ? [...formData.preferredIndustries, industry]
                          : formData.preferredIndustries.filter(i => i !== industry);
                        updateFormData({ preferredIndustries: newIndustries });
                      }}
                      className="h-4 w-4 text-primary-lightGreen focus:ring-primary-lightGreen border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">{industry}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="LinkedIn Profile"
                name="linkedinUrl"
                type="url"
                value={formData.linkedinUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ linkedinUrl: e.target.value })}
                placeholder="https://linkedin.com/in/yourusername"
              />
              <Input
                label="GitHub Profile"
                name="githubUrl"
                type="url"
                value={formData.githubUrl}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ githubUrl: e.target.value })}
                placeholder="https://github.com/yourusername"
              />
            </div>

            <Input
              label="Portfolio Website"
              name="portfolioUrl"
              type="url"
              value={formData.portfolioUrl}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ portfolioUrl: e.target.value })}
              placeholder="https://yourportfolio.com"
            />
          </div>
        );

      case 3: // Complete
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-primary-black mb-2">Profile Setup Complete!</h2>
              <p className="text-gray-600">Your profile has been created successfully. You can now start exploring job opportunities.</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Professional Headline:</span>
                  <span className="font-medium">{formData.professionalHeadline}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Experience:</span>
                  <span className="font-medium">{formData.experience}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Job Types:</span>
                  <span className="font-medium">{formData.preferredJobTypes.join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Work Modes:</span>
                  <span className="font-medium">{formData.preferredWorkModes.join(', ')}</span>
                </div>
              </div>
            </div>

            <div className="bg-primary-teaGreen/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-primary-black mb-2">What's Next?</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-primary-lightGreen mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Browse and apply to job opportunities
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-primary-lightGreen mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Get personalized job recommendations
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-primary-lightGreen mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Track your application status
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 text-primary-lightGreen mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Update your profile anytime
                </li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getProgressPercentage = () => {
    return ((currentStep + 1) / steps.length) * 100;
  };

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-primary-black mb-1">
              Complete Your Profile
            </h1>
            <p className="text-sm md:text-base text-gray-600">
              A few quick steps to unlock tailored job matches
            </p>
          </div>
          <Link to="/user/dashboard" className="hidden sm:block">
            <Button variant="outline" size="sm">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
              Back to Dashboard
            </Button>
          </Link>
        </div>

        {/* Progress and stepper removed (parity with JSX) */}

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-soft p-4 sm:p-6 md:p-8">
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 gap-3">
            <div className="flex space-x-3 mb-4 sm:mb-0">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="w-full sm:w-auto"
              >
                Previous
              </Button>
              {currentStep < steps.length - 1 && (
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="w-full sm:w-auto"
                >
                  Skip for Now
                </Button>
              )}
            </div>

            <div className="flex space-x-3">
              {currentStep === steps.length - 1 ? (
                <Button
                  onClick={handleSubmit} // TODO check type
                  loading={isLoading}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? <Loader size="sm" /> : 'Complete Setup'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="w-full sm:w-auto"
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Need help? <Link to="/contact" className="text-primary-lightGreen hover:text-primary-black font-medium">Contact Support</Link>
          </p>
        </div>
        {(toast) && (
          <Toast
            message={toast.message}
            type={toast.type}
            position="top-right"
            duration={2500}
            onClose={() => setToast(null)}
            show
          />
        )}
      </div>
    </div>
  );
};

export default DashboardForm;
